import { useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { apiClient } from '../../services/http.js';

let _stripePromise = null;

async function getStripePromise() {
    if (_stripePromise) return _stripePromise;
    const { res, data } = await apiClient.request('/billing/config');
    if (!res.ok || !data?.publishable_key) throw new Error('Chave Stripe não disponível');
    _stripePromise = loadStripe(data.publishable_key);
    return _stripePromise;
}

/**
 * Modal de checkout embedded Stripe.
 *
 * Props:
 *   clientSecret  string   — client_secret retornado por POST /billing/checkout
 *   onClose       () => void
 *   onSuccess     () => void  — chamado quando o pagamento é confirmado
 */
export function CheckoutWidget({ clientSecret, onClose, onSuccess }) {
    const [stripePromise, setStripePromise] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const overlayRef = useRef(null);

    // Carrega Stripe promise uma única vez
    useEffect(() => {
        getStripePromise()
            .then(setStripePromise)
            .catch(() => setLoadError('Não foi possível carregar o checkout. Tente novamente.'));
    }, []);

    // Trava scroll do body enquanto modal está aberto
    useEffect(() => {
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previous;
        };
    }, []);

    // Fecha com ESC
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    // Fecha ao clicar no overlay (fora do modal)
    const handleOverlayClick = (e) => {
        if (e.target === overlayRef.current) onClose();
    };

    return (
        <div
            ref={overlayRef}
            onClick={handleOverlayClick}
            className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Checkout"
        >
            <div className="bg-surface-container-low rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-xl overflow-hidden">
                {/* Cabeçalho */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-outline-variant shrink-0">
                    <h2 className="text-lg font-bold">Finalizar Assinatura</h2>
                    <button
                        onClick={onClose}
                        aria-label="Fechar"
                        className="text-on-surface-variant hover:text-on-surface transition-colors text-2xl leading-none"
                    >
                        ✕
                    </button>
                </div>

                {/* Corpo */}
                <div className="flex-1 overflow-y-auto p-4">
                    {loadError ? (
                        <p className="text-error text-sm text-center py-8">{loadError}</p>
                    ) : !stripePromise ? (
                        <p className="text-on-surface-variant text-sm text-center py-8">Carregando...</p>
                    ) : (
                        <EmbeddedCheckoutProvider
                            stripe={stripePromise}
                            options={{ clientSecret }}
                        >
                            <EmbeddedCheckout />
                        </EmbeddedCheckoutProvider>
                    )}
                </div>
            </div>
        </div>
    );
}
